import React, { useEffect, useCallback, forwardRef, useImperativeHandle, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Link from "@tiptap/extension-link";
import Color from "@tiptap/extension-color";
import Placeholder from "@tiptap/extension-placeholder";
import FontSize from "@tiptap/extension-font-size";
import Image from "@tiptap/extension-image";

// Moderne Icons als SVG-Komponenten
const Icons = {
  bold: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 4h8a4 4 0 014 4 4 4 0 01-4 4H6z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12h9a4 4 0 014 4 4 4 0 01-4 4H6z" />
    </svg>
  ),
  italic: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 4h4m-2 0v16m-4 0h8" transform="skewX(-10)" />
    </svg>
  ),
  underline: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 3v7a6 6 0 0012 0V3M4 21h16" />
    </svg>
  ),
  alignLeft: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 6h18M3 12h12M3 18h18" />
    </svg>
  ),
  alignCenter: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 6h18M6 12h12M3 18h18" />
    </svg>
  ),
  alignRight: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 6h18M9 12h12M3 18h18" />
    </svg>
  ),
  bulletList: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  ),
  orderedList: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 6h13M7 12h13M7 18h13M3 6v2m0-2l1 1m-1-1l-1 1M3 18v-2m0 2l1-1m-1 1l-1-1M4 11h1v2H3" />
    </svg>
  ),
  link: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  ),
  unlink: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1M6 18L18 6" />
    </svg>
  ),
  undo: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a5 5 0 015 5v2M3 10l4-4m-4 4l4 4" />
    </svg>
  ),
  redo: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 10H11a5 5 0 00-5 5v2m15-7l-4-4m4 4l-4 4" />
    </svg>
  ),
  code: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
    </svg>
  ),
  image: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
};

// Verfügbare Platzhalter
const DEFAULT_PLACEHOLDERS = [
  { key: "vorname", label: "Vorname", description: "Vorname des Kindes" },
  { key: "nachname", label: "Nachname", description: "Nachname des Kindes" },
  { key: "gruppe", label: "Gruppe", description: "Name der Gruppe" },
];

// Schriftgrößen-Optionen
const FONT_SIZES = [
  { value: "", label: "Standard" },
  { value: "10px", label: "10px" },
  { value: "12px", label: "12px" },
  { value: "14px", label: "14px" },
  { value: "16px", label: "16px" },
  { value: "18px", label: "18px" },
  { value: "20px", label: "20px" },
  { value: "24px", label: "24px" },
  { value: "28px", label: "28px" },
  { value: "32px", label: "32px" },
];

// Toolbar Button Komponente
function ToolbarButton({ active, onClick, children, title, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`flex h-8 w-8 items-center justify-center rounded-lg transition-all ${
        active
          ? "bg-violet-100 text-violet-700"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
    >
      {children}
    </button>
  );
}

// Toolbar Divider
function ToolbarDivider() {
  return <div className="mx-1 h-6 w-px bg-slate-200" />;
}

// Hauptkomponente
const ModernRichTextEditor = forwardRef(function ModernRichTextEditor(
  {
    value,
    onChange,
    placeholder = "Text eingeben...",
    colors = ["#000000", "#1f2937", "#0f766e", "#2563eb", "#b91c1c"],
    placeholders = DEFAULT_PLACEHOLDERS,
    showPlaceholders = true,
    minHeight = "200px",
    className = "",
  },
  ref
) {
  const [showHtmlView, setShowHtmlView] = useState(false);
  const [htmlCode, setHtmlCode] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      TextStyle,
      FontSize,
      Color.configure({ types: ["textStyle"] }),
      Link.configure({
        openOnClick: false,
        linkOnPaste: true,
        autolink: true,
        defaultProtocol: "https",
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
        alignments: ["left", "center", "right"],
      }),
      Placeholder.configure({
        placeholder,
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: 'editor-image',
        },
      }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none",
        style: `min-height: ${minHeight}; padding: 1rem;`,
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (html !== value) {
        onChange?.(html);
      }
    },
  });

  // Expose editor methods via ref
  useImperativeHandle(ref, () => ({
    insertContent: (content) => {
      if (editor) {
        editor.chain().focus().insertContent(content).run();
      }
    },
    insertPlaceholder: (key) => {
      if (editor) {
        editor.chain().focus().insertContent(`{{${key}}}`).run();
      }
    },
    getHTML: () => editor?.getHTML() || "",
    getText: () => editor?.getText() || "",
    focus: () => editor?.chain().focus().run(),
    clear: () => editor?.commands.clearContent(),
  }));

  // Sync external value changes
  useEffect(() => {
    if (editor && typeof value === "string" && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  // Sync HTML view with editor
  useEffect(() => {
    if (showHtmlView && editor) {
      setHtmlCode(editor.getHTML());
    }
  }, [showHtmlView, editor]);

  // Insert placeholder at cursor position
  const insertPlaceholder = useCallback(
    (key) => {
      if (editor) {
        editor.chain().focus().insertContent(`{{${key}}}`).run();
      }
    },
    [editor]
  );

  // Set link
  const setLink = useCallback(() => {
    const previousUrl = editor?.getAttributes("link").href;
    const url = window.prompt("Link-URL eingeben:", previousUrl || "https://");
    
    if (url === null) return;
    
    if (url === "") {
      editor?.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    
    editor?.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  // Insert image from URL
  const insertImage = useCallback(() => {
    const url = window.prompt("Bild-URL eingeben:", "https://");
    
    if (url === null || url === "") return;
    
    // Validate URL
    try {
      new URL(url);
    } catch {
      alert("Bitte geben Sie eine gültige URL ein.");
      return;
    }
    
    editor?.chain().focus().setImage({ src: url }).run();
  }, [editor]);

  // Toggle HTML view
  const toggleHtmlView = () => {
    if (showHtmlView) {
      // Switching back to WYSIWYG - apply HTML changes
      if (editor && htmlCode !== editor.getHTML()) {
        editor.commands.setContent(htmlCode, false);
        onChange?.(htmlCode);
      }
    } else {
      // Switching to HTML view
      setHtmlCode(editor?.getHTML() || "");
    }
    setShowHtmlView(!showHtmlView);
  };

  // Handle HTML code changes
  const handleHtmlChange = (e) => {
    setHtmlCode(e.target.value);
  };

  // Apply HTML changes
  const applyHtmlChanges = () => {
    if (editor) {
      editor.commands.setContent(htmlCode, false);
      onChange?.(htmlCode);
    }
  };

  // Get current font size
  const getCurrentFontSize = () => {
    if (!editor) return "";
    const attrs = editor.getAttributes("textStyle");
    return attrs.fontSize || "";
  };

  // Set font size
  const setFontSize = (size) => {
    if (!editor) return;
    if (size === "") {
      editor.chain().focus().unsetFontSize().run();
    } else {
      editor.chain().focus().setFontSize(size).run();
    }
  };

  if (!editor) {
    return (
      <div className={`rounded-xl border border-slate-200 bg-white ${className}`}>
        <div className="flex h-12 items-center justify-center">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-violet-600" />
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden ${className}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-100 bg-slate-50/50 px-2 py-1.5">
        {/* Text Style */}
        <div className="flex items-center">
          <select
            value={
              editor.isActive("heading", { level: 1 })
                ? "h1"
                : editor.isActive("heading", { level: 2 })
                ? "h2"
                : editor.isActive("heading", { level: 3 })
                ? "h3"
                : "p"
            }
            onChange={(e) => {
              const val = e.target.value;
              if (val === "p") {
                editor.chain().focus().setParagraph().run();
              } else {
                const level = parseInt(val.replace("h", ""));
                editor.chain().focus().toggleHeading({ level }).run();
              }
            }}
            className="h-8 rounded-lg border-0 bg-transparent px-2 text-xs font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="p">Normal</option>
            <option value="h1">Überschrift 1</option>
            <option value="h2">Überschrift 2</option>
            <option value="h3">Überschrift 3</option>
          </select>
        </div>

        {/* Font Size */}
        <div className="flex items-center">
          <select
            value={getCurrentFontSize()}
            onChange={(e) => setFontSize(e.target.value)}
            className="h-8 rounded-lg border-0 bg-transparent px-2 text-xs font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500"
            title="Schriftgröße"
          >
            {FONT_SIZES.map((size) => (
              <option key={size.value} value={size.value}>
                {size.label}
              </option>
            ))}
          </select>
        </div>

        <ToolbarDivider />

        {/* Basic Formatting */}
        <ToolbarButton
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Fett (Ctrl+B)"
        >
          {Icons.bold}
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Kursiv (Ctrl+I)"
        >
          {Icons.italic}
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Unterstrichen (Ctrl+U)"
        >
          {Icons.underline}
        </ToolbarButton>

        <ToolbarDivider />

        {/* Alignment */}
        <ToolbarButton
          active={editor.isActive({ textAlign: "left" })}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          title="Linksbündig"
        >
          {Icons.alignLeft}
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive({ textAlign: "center" })}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          title="Zentriert"
        >
          {Icons.alignCenter}
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive({ textAlign: "right" })}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          title="Rechtsbündig"
        >
          {Icons.alignRight}
        </ToolbarButton>

        <ToolbarDivider />

        {/* Lists */}
        <ToolbarButton
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Aufzählung"
        >
          {Icons.bulletList}
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Nummerierte Liste"
        >
          {Icons.orderedList}
        </ToolbarButton>

        <ToolbarDivider />

        {/* Link */}
        <ToolbarButton
          active={editor.isActive("link")}
          onClick={setLink}
          title="Link einfügen"
        >
          {Icons.link}
        </ToolbarButton>
        {editor.isActive("link") && (
          <ToolbarButton
            onClick={() => editor.chain().focus().unsetLink().run()}
            title="Link entfernen"
          >
            {Icons.unlink}
          </ToolbarButton>
        )}

        {/* Image */}
        <ToolbarButton
          onClick={insertImage}
          title="Bild einfügen (URL)"
        >
          {Icons.image}
        </ToolbarButton>

        <ToolbarDivider />

        {/* Color */}
        <div className="flex items-center gap-1">
          {colors.map((color, idx) => (
            <button
              key={color}
              type="button"
              onClick={() => editor.chain().focus().setColor(color).run()}
              title={`Farbe ${idx + 1}`}
              className="h-6 w-6 rounded-md border border-slate-200 transition-transform hover:scale-110"
              style={{ backgroundColor: color }}
            />
          ))}
        </div>

        <ToolbarDivider />

        {/* Undo/Redo */}
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Rückgängig (Ctrl+Z)"
        >
          {Icons.undo}
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Wiederholen (Ctrl+Y)"
        >
          {Icons.redo}
        </ToolbarButton>

        <ToolbarDivider />

        {/* HTML View Toggle */}
        <ToolbarButton
          active={showHtmlView}
          onClick={toggleHtmlView}
          title={showHtmlView ? "Zurück zum Editor" : "HTML-Ansicht"}
        >
          {Icons.code}
        </ToolbarButton>
      </div>

      {/* Placeholders Bar */}
      {showPlaceholders && placeholders.length > 0 && !showHtmlView && (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 bg-gradient-to-r from-violet-50 to-white px-3 py-2">
          <span className="text-xs font-medium text-violet-700">Platzhalter:</span>
          {placeholders.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => insertPlaceholder(p.key)}
              title={p.description}
              className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2.5 py-1 text-xs font-medium text-violet-700 transition-all hover:bg-violet-200 hover:scale-105"
            >
              <span className="text-violet-400">{"{{"}</span>
              {p.label}
              <span className="text-violet-400">{"}}"}</span>
            </button>
          ))}
        </div>
      )}

      {/* Editor Content or HTML View */}
      <div className="relative">
        {showHtmlView ? (
          <div className="p-2">
            <textarea
              value={htmlCode}
              onChange={handleHtmlChange}
              className="w-full rounded-lg border border-slate-200 bg-slate-900 p-3 font-mono text-sm text-emerald-400 focus:outline-none focus:ring-2 focus:ring-violet-500"
              style={{ minHeight }}
              placeholder="HTML-Code hier eingeben..."
            />
            <div className="mt-2 flex items-center justify-between">
              <p className="text-xs text-slate-500">
                HTML-Code bearbeiten. Änderungen werden beim Wechsel zur Editor-Ansicht übernommen.
              </p>
              <button
                type="button"
                onClick={applyHtmlChanges}
                className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-200"
              >
                HTML übernehmen
              </button>
            </div>
          </div>
        ) : (
          <EditorContent editor={editor} />
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/50 px-3 py-1.5">
        <span className="text-xs text-slate-400">
          {showHtmlView ? "HTML-Modus" : `${editor.getText().length} Zeichen`}
        </span>
        {showHtmlView && (
          <span className="text-xs text-amber-600">⚠ Änderungen im HTML-Code können das Layout beeinflussen</span>
        )}
      </div>

      {/* Styles for TipTap */}
      <style>{`
        .ProseMirror {
          min-height: ${minHeight};
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          color: #94a3b8;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .ProseMirror:focus {
          outline: none;
        }
        .ProseMirror h1 {
          font-size: 1.75rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }
        .ProseMirror h2 {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }
        .ProseMirror h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }
        .ProseMirror p {
          margin-bottom: 0.5rem;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .ProseMirror a {
          color: #2563eb;
          text-decoration: underline;
        }
        .ProseMirror img.editor-image {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 0.5rem 0;
          cursor: pointer;
        }
        .ProseMirror img.editor-image.ProseMirror-selectednode {
          outline: 3px solid #8b5cf6;
          outline-offset: 2px;
        }
      `}</style>
    </div>
  );
});

export default ModernRichTextEditor;
